import {Activity, AlertCircle} from "lucide-react";
import {averageLoadLevel} from "../../../utils/NewAlternativeView/utils";
import {PerformanceMetricItem} from "../../components/NewAlternativeView/PerformanceMetricItem";
import {Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import React from "react";
import {ApiBankInfo, ApiBuildingInfo, ApiDeviceInfo} from "./solutrakApiService";
import {PerformanceData} from "../../../types/performance";
import {BankAndDeviceSelectors} from "../../components/NewAlternativeView/BankAndDeviceSelectors";

type PerformanceDataViewProps = {
    selectedBuilding: ApiBuildingInfo | null;
    selectedBank: ApiBankInfo | null;
    selectedDevice: ApiDeviceInfo | null;
    isLoadingPerformance: boolean;
    performanceData: PerformanceData;
    performanceError: string | null;
    handleBankSelect: (bank: ApiBankInfo) => void;
    handleDeviceSelect: (device: ApiDeviceInfo) => void;
}

export default function PerformanceDataView({selectedBuilding, selectedBank, selectedDevice, isLoadingPerformance, performanceData, performanceError, handleBankSelect, handleDeviceSelect}: PerformanceDataViewProps) {
    return (
        <>
            {/* Bank and Device Selectors for Performance */}
            <BankAndDeviceSelectors
                selectedBuilding={selectedBuilding}
                selectedBank={selectedBank}
                selectedDevice={selectedDevice}
                handleBankSelect={handleBankSelect}
                handleDeviceSelect={handleDeviceSelect} />

            {/* Performance Tab Content */}
            {(() => {
                if (!selectedDevice) {
                    return (
                        <div className="p-6 text-center text-slate-400">
                            Please select a device to view its performance data.
                        </div>
                    );
                }

                if (isLoadingPerformance) {
                    return (
                        <div className="p-6 text-center text-slate-400">
                            <Activity className="animate-spin inline-block mr-2" size={20}/>
                            Loading performance data...
                        </div>
                    );
                }

                if (performanceError) {
                    return (
                        <div className="p-6 text-center text-red-400">
                            <AlertCircle className="inline-block mr-2" size={20}/>
                            Error loading performance data: {performanceError}
                        </div>
                    );
                }

                if (!performanceData) {
                    return (
                        <div className="p-6 text-center text-slate-400">
                            No performance data available for the selected device and period.
                        </div>
                    );
                }

                // Calculate aggregated values
                const totalRuns = performanceData.runCounts?.reduce((sum, item) => sum + item.Total, 0) || 0;
                const totalUps = performanceData.runCounts?.reduce((sum, item) => sum + item.Up, 0) || 0;
                const totalDowns = performanceData.runCounts?.reduce((sum, item) => sum + item.Down, 0) || 0;
                // TODO(Fix): Incorrect - Use door cycle data instead
                // const totalDoorCycles = performanceData.doorCyclesPerFloor?.reduce((sum, item) => sum + item.Close, 0) || 0;

                const averageLoad = performanceData.loadLevels?.length > 0
                    ? averageLoadLevel(performanceData.loadLevels)
                    : null;

                const hourlyCallsMax = performanceData.hourlyCarCalls?.length > 0
                    ? Math.max(...performanceData.hourlyCarCalls.map(c => c.Count), 0)
                    : 0;

                return (
                    <div className="p-6 space-y-6">
                        <h2 className="text-2xl font-semibold text-white">Performance
                            Overview: {selectedDevice.deviceName}</h2>

                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            <PerformanceMetricItem label="Total Runs" value={totalRuns}/>
                            <PerformanceMetricItem label="Up Runs" value={totalUps}/>
                            <PerformanceMetricItem label="Down Runs" value={totalDowns}/>
                            {/*<PerformanceMetricItem label="Total Door Cycles" value={totalDoorCycles} />*/}
                            <PerformanceMetricItem label="Avg. Load Level" value={averageLoad}/>
                            {performanceData.doorOpenTime &&
                                <PerformanceMetricItem
                                    label="Door Open Time"
                                    value={performanceData.doorOpenTime}
                                    unit="s"/>
                            }
                            {performanceData.doorCloseTime &&
                                <PerformanceMetricItem
                                    label="Door Close Time"
                                    value={performanceData.doorCloseTime}
                                    unit="s"/>
                            }
                            {performanceData.accelerationRate &&
                                <PerformanceMetricItem
                                    label="Acceleration Rate"
                                    value={performanceData.accelerationRate}
                                    unit="m/s²"/>

                            }
                            {performanceData.jerkRate &&
                                <PerformanceMetricItem
                                    label="Jerk Rate"
                                    value={performanceData.jerkRate}
                                    unit="m/s³"/>
                            }

                            {performanceData.maxSpeed &&
                                <PerformanceMetricItem
                                    label="Max Speed"
                                    value={performanceData.maxSpeed}
                                    unit="m/s"/>
                            }
                        </div>

                        {performanceData.hourlyCarCalls && performanceData.hourlyCarCalls.length > 0 && (
                            <div className="bg-slate-800 p-4 rounded-lg shadow">
                                <h3 className="text-lg font-medium text-white mb-3">Car Calls (Last 24
                                    Hours)</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart
                                        data={performanceData.hourlyCarCalls.map((d) => ({
                                            hour: new Date(d.TimeSlot).getHours(),
                                            count: Number(d.Count),
                                        }))}
                                        margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#475569"/>
                                        <XAxis
                                            dataKey="hour"
                                            stroke="#94a3b8"
                                            fontSize={12}
                                            tickFormatter={(val) => `${val}:00`}
                                        />
                                        <YAxis stroke="#94a3b8" fontSize={12}/>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                border: 'none',
                                                borderRadius: '0.375rem'
                                            }}
                                            labelFormatter={(label) => `${label}:00`}
                                            formatter={(val: number) => [`${val} calls`, 'Calls']}
                                        />
                                        <Bar dataKey="count" fill="#38bdf8"/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Door Cycles Per Floor */}
                        {performanceData.doorCyclesPerFloor && performanceData.doorCyclesPerFloor.length > 0 && (
                            <div className="bg-slate-800 p-4 rounded-lg shadow mt-4">
                                <h3 className="text-lg font-medium text-white mb-3">Door Cycles Per Floor
                                    (Top 5)</h3>
                                <ul className="space-y-1">
                                    {performanceData.doorCyclesPerFloor
                                        .sort((a, b) => Number(b.Count) - Number(a.Count))
                                        .slice(0, 5)
                                        .map((item, index) => (
                                            <li key={index}
                                                className="text-sm text-slate-300 flex justify-between">
                                                <span>Floor {item.Floor}:</span>
                                                <span
                                                    className="font-medium text-white">{item.Count} cycles</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}

                        {/* Temperature Data Points */}
                        {performanceData.controllerTemperature && (
                            <div className="bg-slate-800 p-4 rounded-lg shadow mt-4">
                                <h3 className="text-lg font-medium text-white mb-3">Controller
                                    Temperature</h3>

                                <div className="mb-3 text-sm text-slate-300">
                                    <strong className="text-white">Max Temp:</strong>{' '}
                                    {performanceData.controllerTemperature.MaxCelsius}°C (
                                    {performanceData.controllerTemperature.MaxFahrenheit}°F)
                                </div>

                                {performanceData.controllerTemperature.Logs.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart
                                            data={performanceData.controllerTemperature.Logs.map(log => ({
                                                time: new Date(log.Date).toLocaleTimeString([], {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }),
                                                temperature: log.TemperatureCelsius,
                                            }))}
                                            margin={{top: 10, right: 30, left: 10, bottom: 0}}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#475569"/>
                                            <XAxis dataKey="time" stroke="#94a3b8" fontSize={12}/>
                                            <YAxis
                                                unit="°C"
                                                stroke="#94a3b8"
                                                fontSize={12}
                                                domain={[0, 'dataMax + 10']}
                                                tickFormatter={(value) => `${value}°`}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#1e293b',
                                                    border: 'none',
                                                    borderRadius: '0.375rem'
                                                }}
                                                labelStyle={{color: '#f1f5f9'}}
                                                formatter={(value: number) => [`${value}°C`, 'Temperature']}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="temperature"
                                                stroke="#f43f5e"
                                                strokeWidth={2}
                                                dot={{
                                                    r: 4,
                                                    fill: '#f43f5e',
                                                    stroke: '#f43f5e',
                                                    strokeWidth: 1,
                                                }}
                                                activeDot={{
                                                    r: 6,
                                                    stroke: '#f43f5e',
                                                    strokeWidth: 2,
                                                    fill: '#fef2f2',
                                                }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="text-slate-400 italic">No temperature logs
                                        available.</div>
                                )}
                            </div>
                        )}

                        {/* Uptime Data Points */}
                        {performanceData.uptimeStats && performanceData.uptimeStats.length > 0 && (
                            <div className="bg-slate-800 p-4 rounded-lg shadow mt-4">
                                <h3 className="text-lg font-medium text-white mb-3">Uptime/Downtime
                                    Periods</h3>
                                <div className="max-h-60 overflow-y-auto">
                                    <ul className="space-y-2">
                                        {performanceData.uptimeStats.map((stat, index) => {
                                            const totalSeconds =
                                                stat.actualUptime +
                                                stat.scheduledMaintenance +
                                                stat.scheduledShutdown +
                                                stat.unscheduledMaintenance +
                                                stat.unscheduledRepair;

                                            const segments = [
                                                {
                                                    label: "Uptime",
                                                    value: stat.actualUptime,
                                                    color: "bg-emerald-500"
                                                },
                                                {
                                                    label: "Scheduled Maintenance",
                                                    value: stat.scheduledMaintenance,
                                                    color: "bg-blue-500"
                                                },
                                                {
                                                    label: "Scheduled Shutdown",
                                                    value: stat.scheduledShutdown,
                                                    color: "bg-indigo-500"
                                                },
                                                {
                                                    label: "Unscheduled Maintenance",
                                                    value: stat.unscheduledMaintenance,
                                                    color: "bg-yellow-500"
                                                },
                                                {
                                                    label: "Unscheduled Repair",
                                                    value: stat.unscheduledRepair,
                                                    color: "bg-red-500"
                                                },
                                            ];

                                            return (
                                                <div key={index} className="mb-6">
                                                    <h4 className="text-white text-base font-semibold mb-2">{stat.title}</h4>

                                                    <div
                                                        className="h-4 w-full bg-slate-700 rounded overflow-hidden flex">
                                                        {segments.map(
                                                            (seg, i) =>
                                                                seg.value > 0 && (
                                                                    <div
                                                                        key={i}
                                                                        className={`${seg.color} h-full`}
                                                                        style={{width: `${(seg.value / totalSeconds) * 100}%`}}
                                                                        title={`${seg.label}: ${(seg.value / 60).toFixed(1)} mins`}
                                                                    />
                                                                )
                                                        )}
                                                    </div>

                                                    <div
                                                        className="flex flex-wrap gap-4 mt-2 text-sm text-slate-300">
                                                        {segments.map(
                                                            (seg, i) =>
                                                                seg.value > 0 && (
                                                                    <div key={i}
                                                                         className="flex items-center space-x-2">
                                                                                    <span
                                                                                        className={`w-3 h-3 inline-block rounded ${seg.color}`}></span>
                                                                        <span>
                  {seg.label}: {(seg.value / 60).toFixed(1)} mins
                </span>
                                                                    </div>
                                                                )
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}
        </>
    );
}
